import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle/index';
import Button from '@components/shared/Button/Button';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { useListProjectChecklistQuery } from '@graphql/generated';

export const ChecklistActionBar = ({
  totalCheckedCount,
  creatingChecklist,
  onCreateChecklist,
  disabled = false,
  marginHorizontal = 'm',
  marginBottom = 'm',
}: {
  totalCheckedCount: number;
  creatingChecklist: boolean;
  onCreateChecklist: () => void;
  disabled: boolean;
  marginHorizontal?: any;
  marginBottom?: any;
}) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  const { idProjectChecklistTemplateOpen } = useWebDrawer();

  const { data: projectChecklistData } = useListProjectChecklistQuery({
    fetchPolicy: 'cache-first',
    variables: {
      projectUUId: idProjectChecklistTemplateOpen,
    },
    skip: !idProjectChecklistTemplateOpen,
  });

  const buttonTitle =
    (projectChecklistData?.listProjectChecklist?.length ?? 0) > 0
      ? t('models:tasks.buttons.addItem')
      : t('models:projects.checklist.createChecklist');

  const disableColor = Platform.OS === 'web' ? 'grey04' : 'textSecondary';
  return (
    <Box
      backgroundColor='white'
      style={[
        {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          paddingBottom: insets.bottom,
        },
        Platform.OS === 'ios'
          ? {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: -5 },
              shadowOpacity: 0.05,
              shadowRadius: 25,
            }
          : {
              elevation: 0,
            },
      ]}>
      {Platform.OS === 'android' && (
        <LinearGradient
          colors={['rgba(0,0,0,0.05)', 'transparent']}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 20,
          }}
        />
      )}
      <Button
        marginHorizontal={marginHorizontal}
        marginTop='xs'
        // `isLoading` alone already locks presses (Button.tsx) while
        // keeping the normal color so the spinner reads as "processing" —
        // also setting `disabled` here doubled it up with the grey
        // disabled look underneath the spinner (aprillv PR review #5803).
        disabled={disabled || totalCheckedCount === 0}
        isLoading={creatingChecklist}
        marginBottom={marginBottom}
        accessibilityLabel={t('models:projects.checklist.createChecklist')}
        backgroundColor={totalCheckedCount > 0 ? 'black' : 'grey02'}
        // No `variant` is passed to this Button, so its own text-color
        // switch falls through to the default case ('black') — invisible
        // against this button's black background while `isLoading` swaps
        // in the shared Spinner, which colors itself from that same
        // resolved color (QA finding, TA-10387). Pin it to the same color
        // the label itself uses below so the spinner is actually visible.
        btnTextColor={totalCheckedCount > 0 ? 'white' : disableColor}
        fullWidth
        height={56}
        onPress={onCreateChecklist}>
        <Text
          variant='buttonLabel'
          textAlign='center'
          color={totalCheckedCount > 0 ? 'white' : disableColor}>
          {buttonTitle} ({totalCheckedCount})
        </Text>
      </Button>
    </Box>
  );
};
